import React, { useState, useEffect, useCallback, useMemo, useContext } from 'react';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay, parseISO, addDays, startOfMonth, endOfMonth, startOfYear, endOfYear, eachMonthOfInterval, eachDayOfInterval, isSameMonth, isSameDay, isWithinInterval } from 'date-fns';
import { es } from 'date-fns/locale';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Avatar from '@mui/material/Avatar';
import Autocomplete from '@mui/material/Autocomplete';
import Checkbox from '@mui/material/Checkbox';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import EventIcon from '@mui/icons-material/Event';
import PersonIcon from '@mui/icons-material/Person';
import BeachAccessIcon from '@mui/icons-material/BeachAccess';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import WorkIcon from '@mui/icons-material/Work';
import PeopleIcon from '@mui/icons-material/People';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TodayIcon from '@mui/icons-material/Today';
import IconButton from '@mui/material/IconButton';
import Alert from '@mui/material/Alert';
import { ToastContainer, toast } from 'react-toastify';
import { Download } from 'lucide-react';
import { fetchCalendarioVacaciones, fetchExportarCalendario, fetchDashboardStats, actualizarSolicitud, fetchFestivosPersonas } from '../../peticiones_API/PeticionesVacaciones';
import { fetchFestivosCombinados, fetchFestivosByCalendario, fetchFestivosByCalendarioColab } from '../../peticiones_API/PeticionesFestivos';
import { fetchPersonasData, fetchNegociosData, fetchDepartamentosData } from '../../peticiones_API/PeticionesGestionPersonasPersonas';
import { fetchPersonasColaboradorasData } from '../../peticiones_API/PeticionesPersonasColaboradoras';
import { fetchEmpresasColaboradorasData } from '../../peticiones_API/PeticionesEmpresasColaboradoras';
import { fetchPeriodosVerano } from '../../peticiones_API/PeticionesPeriodosVerano';
import { fetchPeriodosVeranoColab } from '../../peticiones_API/PeticionesPeriodosVeranoColab';
import { UsuarioContext } from '../providers/personaContext';
import {Eraser  } from 'lucide-react';
import GestorCalendariosLaborales from './GestorCalendariosLaborales';
import './GestorCalendarioVacaciones.css';

const API_URL = process.env.REACT_APP_API_URL;
const locales = { es };

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

const COLORES_DEPARTAMENTO = [
  '#2196F3', // Azul
  '#4CAF50', // Verde
  '#FF9800', // Naranja
  '#9C27B0', // Púrpura
  '#00BCD4', // Cyan
  '#F44336', // Rojo
  '#795548', // Marrón
  '#607D8B', // Gris azulado
  '#E91E63', // Rosa
  '#3F51B5', // Índigo
  '#009688', // Teal
  '#CDDC39', // Lima
];

const getColorDepartamento = (departamentoId) => {
  if (!departamentoId) return '#4caf50';
  return COLORES_DEPARTAMENTO[departamentoId % COLORES_DEPARTAMENTO.length];
};

// Rango [desde, hasta] (YYYY-MM-DD) visible segun la vista y la fecha ancla del calendario.
// Semana: lunes-domingo. Se usa para el nombre del Excel y para recortar los eventos del mes.
const getRangoVisible = (view, date) => {
  const fmt = (d) => format(d, 'yyyy-MM-dd');
  if (view === 'year') return { desde: fmt(startOfYear(date)), hasta: fmt(endOfYear(date)) };
  if (view === 'week') { const ini = startOfWeek(date, { weekStartsOn: 1 }); return { desde: fmt(ini), hasta: fmt(addDays(ini, 6)) }; }
  if (view === 'day') return { desde: fmt(date), hasta: fmt(date) };
  return { desde: fmt(startOfMonth(date)), hasta: fmt(endOfMonth(date)) };
};

const YearView = ({ date, events, onNavigate, onView, onSelectEvent, onSelectDayEvents }) => {
  const year = date.getFullYear();
  const months = eachMonthOfInterval({
    start: startOfYear(date),
    end: endOfYear(date)
  });

  const diasSemana = ['L', 'M', 'X', 'J', 'V', 'S', 'D'];

  const getEventsForDay = (day) => {
    return events.filter(event => {
      const eventStart = new Date(event.start);
      const eventEnd = new Date(event.end);
      eventEnd.setDate(eventEnd.getDate() - 1);
      return isWithinInterval(day, { start: eventStart, end: eventEnd }) ||
             isSameDay(day, eventStart);
    });
  };

  const handleMonthClick = (monthDate) => {
    onNavigate(monthDate);
    onView('month');
  };

  const handlePrevYear = () => {
    const newDate = new Date(date);
    newDate.setFullYear(year - 1);
    onNavigate(newDate);
  };

  const handleNextYear = () => {
    const newDate = new Date(date);
    newDate.setFullYear(year + 1);
    onNavigate(newDate);
  };

  const handleToday = () => {
    onNavigate(new Date());
  };

  return (
    <Box className="year-view-container">
      <Box sx={{
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 1,
        mb: 2,
        pb: 1,
        borderBottom: '1px solid #e0e0e0'
      }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Button
            variant="outlined"
            size="small"
            onClick={handleToday}
            startIcon={<TodayIcon />}
            sx={{ textTransform: 'none' }}
          >
            Hoy
          </Button>
          <IconButton onClick={handlePrevYear} size="small">
            <ChevronLeftIcon />
          </IconButton>
          <IconButton onClick={handleNextYear} size="small">
            <ChevronRightIcon />
          </IconButton>
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#336385', display: { xs: 'block', md: 'none' } }}>
            {year}
          </Typography>
        </Box>
        <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#336385', display: { xs: 'none', md: 'block' } }}>
          {year}
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
          <Button
            variant="contained"
            size="small"
            sx={{ textTransform: 'none', backgroundColor: '#336385', minWidth: 'auto', px: 1 }}
          >
            Año
          </Button>
          <Button
            variant="text"
            size="small"
            onClick={() => onView('month')}
            sx={{ textTransform: 'none', minWidth: 'auto', px: 1 }}
          >
            Mes
          </Button>
          <Button
            variant="text"
            size="small"
            onClick={() => onView('week')}
            sx={{ textTransform: 'none', minWidth: 'auto', px: 1 }}
          >
            Semana
          </Button>
          <Button
            variant="text"
            size="small"
            onClick={() => onView('day')}
            sx={{ textTransform: 'none', minWidth: 'auto', px: 1 }}
          >
            Día
          </Button>
        </Box>
      </Box>

      <Box sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: 2,
        '@media (max-width: 1200px)': {
          gridTemplateColumns: 'repeat(3, 1fr)'
        },
        '@media (max-width: 900px)': {
          gridTemplateColumns: 'repeat(2, 1fr)'
        },
        '@media (max-width: 600px)': {
          gridTemplateColumns: '1fr'
        }
      }}>
        {months.map((monthDate, idx) => {
          const monthStart = startOfMonth(monthDate);
          const monthEnd = endOfMonth(monthDate);
          const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });

          let startDay = getDay(monthStart);
          startDay = startDay === 0 ? 6 : startDay - 1;

          return (
            <Card
              key={idx}
              sx={{
                cursor: 'pointer',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: 3
                }
              }}
              onClick={() => handleMonthClick(monthDate)}
            >
              <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 'bold',
                    textAlign: 'center',
                    mb: 1,
                    color: '#336385',
                    textTransform: 'capitalize'
                  }}
                >
                  {format(monthDate, 'MMMM', { locale: es })}
                </Typography>

                <Box sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(7, 1fr)',
                  gap: 0.25,
                  mb: 0.5
                }}>
                  {diasSemana.map((dia, i) => (
                    <Typography
                      key={i}
                      variant="caption"
                      sx={{
                        textAlign: 'center',
                        fontWeight: 'bold',
                        color: i >= 5 ? '#f44336' : '#666',
                        fontSize: '0.65rem'
                      }}
                    >
                      {dia}
                    </Typography>
                  ))}
                </Box>

                <Box sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(7, 1fr)',
                  gap: 0.25
                }}>
                  {Array.from({ length: startDay }).map((_, i) => (
                    <Box key={`empty-${i}`} sx={{ aspectRatio: '1', minHeight: 20 }} />
                  ))}

                  {daysInMonth.map((day, dayIdx) => {
                    const dayEvents = getEventsForDay(day);
                    const isToday = isSameDay(day, new Date());
                    const isWeekend = getDay(day) === 0 || getDay(day) === 6;

                    const festivoEvent = dayEvents.find(e => e.type === 'festivo');
                    const vacacionEvents = dayEvents.filter(e => e.type === 'vacacion' || e.type === 'festivo_persona');

                    const hexToRgba = (hex, alpha) => {
                      const r = parseInt(hex.slice(1, 3), 16);
                      const g = parseInt(hex.slice(3, 5), 16);
                      const b = parseInt(hex.slice(5, 7), 16);
                      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
                    };

                    let bgColor = 'transparent';
                    let textColor = isWeekend ? '#f44336' : 'inherit';

                    if (isToday) {
                      bgColor = '#336385';
                      textColor = 'white';
                    } else if (festivoEvent) {
                      bgColor = hexToRgba(festivoEvent.color, 0.1);
                      textColor = festivoEvent.color;
                    }

                    return (
                      <Box
                        key={dayIdx}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (dayEvents.length > 0) {
                            onSelectDayEvents(dayEvents, day);
                          }
                        }}
                        sx={{
                          aspectRatio: '1',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: '50%',
                          backgroundColor: bgColor,
                          cursor: dayEvents.length > 0 ? 'pointer' : 'default',
                          position: 'relative',
                          transition: 'all 0.15s ease',
                          '&:hover': dayEvents.length > 0 ? {
                            backgroundColor: festivoEvent && !isToday ? hexToRgba(festivoEvent.color, 0.5) : (isToday ? '#264a63' : '#f0f0f0'),
                            transform: 'scale(1.1)'
                          } : {}
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: festivoEvent ? '0.7rem' : '0.85rem',
                            fontWeight: (isToday || festivoEvent) ? 'bold' : 'normal',
                            color: textColor
                          }}
                        >
                          {format(day, 'd')}
                        </Typography>

                        {vacacionEvents.length > 0 && (
                          <Box sx={{
                            display: 'flex',
                            gap: '1px',
                            position: 'absolute',
                            bottom: 1,
                            maxWidth: '100%',
                            overflow: 'hidden'
                          }}>
                            {vacacionEvents.slice(0, 6).map((event, eIdx) => {
                              // Vista Ano: cuadricula propia, no react-big-calendar. A 5px el rayado
                              // no se lee, asi que aqui pendiente = anillo hueco. box-sizing border-box
                              // mantiene el diametro exterior en 5px: cero cambio de maquetacion.
                              const colorEvento = event.color || '#4caf50';
                              const esPendiente = event?.resource?.estado === 'pendiente';
                              return (
                                <Box
                                  key={eIdx}
                                  sx={{
                                    width: 5,
                                    height: 5,
                                    borderRadius: '50%',
                                    boxSizing: 'border-box',
                                    backgroundColor: esPendiente ? 'transparent' : colorEvento,
                                    border: esPendiente ? `1.5px solid ${colorEvento}` : 'none'
                                  }}
                                />
                              );
                            })}
                          </Box>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              </CardContent>
            </Card>
          );
        })}
      </Box>
    </Box>
  );
};

function GestorCalendarioVacaciones() {
  const { persona, ambito, ambitoCargado, ambitoError, recargarAmbito } = useContext(UsuarioContext);
  const [tabActual, setTabActual] = useState(0);
  const [vacaciones, setVacaciones] = useState([]);
  const [festivos, setFestivos] = useState([]);
  const [festivosPersonas, setFestivosPersonas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('year');
  const [date, setDate] = useState(new Date());
  const [filtroEstado, setFiltroEstado] = useState('aprobada');
  const [filtroVista, setFiltroVista] = useState('personal');
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showEventModal, setShowEventModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editData, setEditData] = useState({
    fecha_inicio: '',
    fecha_fin: '',
    comentarios: ''
  });
  const [esSupervisor, setEsSupervisor] = useState(false);
  const [subordinadosIds, setSubordinadosIds] = useState([]);
  const [dayEvents, setDayEvents] = useState([]);
  const [selectedDay, setSelectedDay] = useState(null);
  const [showDayEventsModal, setShowDayEventsModal] = useState(false);

  const [empresas, setEmpresas] = useState([]);
  const [todosDepartamentos, setTodosDepartamentos] = useState([]);
  const [departamentosFiltrados, setDepartamentosFiltrados] = useState([]);
  const [personas, setPersonas] = useState([]);
  const [personasColab, setPersonasColab] = useState([]);
  const [filtroEmpresa, setFiltroEmpresa] = useState([]);
  const [filtroDepartamento, setFiltroDepartamento] = useState([]);
  const [filtroPersona, setFiltroPersona] = useState([]);
  const [filtroEmpresaAplicado, setFiltroEmpresaAplicado] = useState([]);
  const [filtroDepartamentoAplicado, setFiltroDepartamentoAplicado] = useState([]);
  const [filtroPersonaAplicado, setFiltroPersonaAplicado] = useState([]);
  const [empresaInputText, setEmpresaInputText] = useState('');
  const [departamentoInputText, setDepartamentoInputText] = useState('');
  const [personaInputText, setPersonaInputText] = useState('');
  const [empresaEmpInputText, setEmpresaEmpInputText] = useState('');
  const [departamentoEmpInputText, setDepartamentoEmpInputText] = useState('');
  const [periodosVerano, setPeriodosVerano] = useState([]);
  const [festivosParaCalculo, setFestivosParaCalculo] = useState([]);
  const [calendarioLaboralId, setCalendarioLaboralId] = useState(persona?.calendario_laboral_id || null);
  const calendarioLaboralColabId = persona?.calendario_laboral_colab_id || null;
  // Paso 1 "calendario del compañero": MI calendario (dos ejes) para la marca "distinto al tuyo".
  // SIEMPRE del dashboard, NUNCA del token (calendario_laboral_id se retira en Fase 6).
  const [miCalendario, setMiCalendario] = useState({ int: null, colab: null });

  // Paso 2: modal del calendario del compañero. Reutiliza GET /festivos/calendario/:id (devuelve SOLO
  // festivos, auth-only). Dialog sobre Dialog (si en TEST queda raro con scroll/foco, se cambia a
  // sustituir contenido + flecha de volver). Estados aislados para no tocar el flujo del popover.
  const [showCalModal, setShowCalModal] = useState(false);
  const [calModalInfo, setCalModalInfo] = useState(null); // { calId, calNombre, personaNombre, esColab }
  const [calModalFestivos, setCalModalFestivos] = useState(null);
  const [calModalLoading, setCalModalLoading] = useState(false);
  const [calModalError, setCalModalError] = useState(false);
  const [calModalMisFechas, setCalModalMisFechas] = useState(null); // Set de fechas de MI calendario (mismo eje); marca los festivos suyos que yo no tengo
  const cargarFestivosCalModal = async (cal, esColab, personaNombre) => {
    if (!cal?.id) return;
    setCalModalInfo({ calId: cal.id, calNombre: cal.nombre, personaNombre, esColab });
    setShowCalModal(true); setCalModalLoading(true); setCalModalError(false); setCalModalFestivos(null); setCalModalMisFechas(null);
    try {
      const anioVista = date.getFullYear(); // el año que se está viendo, no el actual
      const fetchFn = esColab ? fetchFestivosByCalendarioColab : fetchFestivosByCalendario;
      const suyos = await fetchFn(cal.id, anioVista);
      setCalModalFestivos(suyos || { nacional: [], empresa: [], provincial: [], municipal: [] });
      // Comparación en la LISTA (no en la etiqueta): marcar los festivos SUYOS que yo NO tengo. Cargo mi
      // propio calendario del MISMO eje (miCalendario, del dashboard), solo si lo tengo y es distinto del
      // suyo (mismo id = nada que comparar; sin calendario propio = sin comparación). Una llamada más,
      // solo al abrir el modal. Se comparan por FECHA: nacional/empresa son globales (coinciden), así que
      // solo destacan los provinciales/municipales que él tiene y yo no.
      const miCalId = esColab ? miCalendario.colab : miCalendario.int;
      if (miCalId && miCalId !== cal.id) {
        const mios = await fetchFn(miCalId, anioVista);
        const fechas = new Set([
          ...(mios.nacional || []), ...(mios.empresa || []), ...(mios.provincial || []), ...(mios.municipal || [])
        ].map(f => f.fecha));
        setCalModalMisFechas(fechas);
      }
    } catch (e) { setCalModalError(true); } finally { setCalModalLoading(false); }
  };
  const [empresasUsuario, setEmpresasUsuario] = useState([]);
  const [empresasSeleccionadas, setEmpresasSeleccionadas] = useState([]);
  const [departamentosEmpresaUsuario, setDepartamentosEmpresaUsuario] = useState([]);
  const [departamentosEmpresaFiltrados, setDepartamentosEmpresaFiltrados] = useState([]);
  const [departamentosEmpresaSeleccionados, setDepartamentosEmpresaSeleccionados] = useState([]);
  const [empresasColaboradoras, setEmpresasColaboradoras] = useState([]);

  // Ámbito de vacaciones resuelto en SERVIDOR. El calendario usa el eje VER (decisión
  // ratificada: VER para calendario, GESTIONAR para bandeja). Solo se migran las ramas de
  // delegado; los fallbacks (esRolAdminGestion por rol, comodín "todas") se conservan. El
  // nivel DEPARTAMENTO del token desaparece (Fase 2 = solo empresa). Los desplegables
  // ampliados reciben {id,nombre} del ámbito directamente (fuera una resolución en cliente).
  const verNegocios = useMemo(() => ambito?.ver?.negocios || [], [ambito]);
  const verColaboradoras = useMemo(() => ambito?.ver?.colaboradoras || [], [ambito]);
  const verNegocioIds = useMemo(() => verNegocios.map(n => n.id), [verNegocios]);
  const verColabIds = useMemo(() => verColaboradoras.map(c => c.id), [verColaboradoras]);

  const tieneVacacionesInt = !!(ambito?.ver?.todosNegocios || verNegocioIds.length > 0);
  const tieneVacacionesColab = !!(ambito?.ver?.todasColaboradoras || verColabIds.length > 0);

  const rolLowerGestion = persona?.rol?.toLowerCase() || '';
  const esRolAdminGestion = ['super administrador', 'administrador', 'recursos humanos'].includes(rolLowerGestion);
  const tieneGestion = tieneVacacionesInt || tieneVacacionesColab;
  const esSoloColab = persona?.es_colaborador && !tieneVacacionesInt && !esRolAdminGestion;

  const handleTabChange = (event, newValue) => {
    setTabActual(newValue);
    const esNuevoTabColab = tabColabIndex !== -1 && newValue === tabColabIndex;
    if (esNuevoTabColab) {
      setFiltroVista((tieneVacacionesColab || esRolAdminGestion) ? 'todos' : 'personal');
    } else {
      setFiltroVista('personal');
    }
    setFiltroEmpresa([]);
    setFiltroDepartamento([]);
    setFiltroPersona([]);
    setFiltroEmpresaAplicado([]);
    setFiltroDepartamentoAplicado([]);
    setFiltroPersonaAplicado([]);
    setEmpresasSeleccionadas([]);
    setDepartamentosEmpresaSeleccionados([]);
  };

  // Fase D: fuera 'operativa' de las tres listas. Ese rol NO EXISTE en la tabla `roles` de ningun
  // entorno (local, TEST y produccion tienen los mismos cinco) y CERO personas lo tienen, asi que
  // ninguna de estas comparaciones podia dar verdadero nunca.
  const esAdministracion = ['general', 'gestor de contenidos'].includes(persona?.rol?.toLowerCase());
  const esColaborador = persona?.es_colaborador || false;
  const rolLower = persona?.rol?.toLowerCase() || '';
  const puedeVerFiltrosAvanzados = ['super administrador', 'administrador', 'recursos humanos'].includes(rolLower);

  // Antes eran tres lineas: una lista que INCLUIA 'operativa' y un `&& !esOperativa` que lo
  // volvia a restar. Se anulaban entre si — el resultado era el mismo incluso en un mundo donde
  // el rol existiera. `puedeVerCalendarioLaboral` y `esOperativa` no se usaban en ningun otro
  // sitio, asi que las tres colapsan en esta.
  const puedeVerLaboral = ['super administrador', 'administrador', 'recursos humanos'].includes(rolLower);

  const tabsConfig = useMemo(() => {
    const tabs = [];
    if (!esSoloColab) {
      tabs.push({ key: 'vacaciones', label: 'Vacaciones', icon: <PeopleIcon /> });
    }
    if (tieneVacacionesColab || esRolAdminGestion || esSoloColab) {
      tabs.push({ key: 'colab', label: 'Vacaciones Colab', icon: <PeopleIcon /> });
    }
    if (puedeVerLaboral) {
      tabs.push({ key: 'laboral', label: 'Laboral', icon: <WorkIcon /> });
      tabs.push({ key: 'laboralColab', label: 'Laboral Colab', icon: <WorkIcon /> });
    }
    return tabs;
  }, [tieneVacacionesColab, esRolAdminGestion, puedeVerLaboral, esSoloColab]);

  const tabColabIndex = tabsConfig.findIndex(t => t.key === 'colab');
  const tabLaboralIndex = tabsConfig.findIndex(t => t.key === 'laboral');
  const tabLaboralColabIndex = tabsConfig.findIndex(t => t.key === 'laboralColab');
  const esTabCalendario = tabActual === 0;
  const esTabColab = tabColabIndex !== -1 && tabActual === tabColabIndex;

  useEffect(() => {
    const cargarDatosAuxiliares = async () => {
      if (!puedeVerFiltrosAvanzados && !tieneGestion) return;
      try {
        const promesas = [
          fetchNegociosData(),
          fetchDepartamentosData(),
          fetchPersonasData()
        ];
        if (tieneVacacionesColab || esRolAdminGestion) {
          promesas.push(fetchEmpresasColaboradorasData());
          promesas.push(fetchPersonasColaboradorasData());
        }
        const resultados = await Promise.all(promesas);
        const [empresasData, departamentosData, personasData] = resultados;
        setEmpresas(empresasData || []);
        setTodosDepartamentos(departamentosData || []);
        setPersonas(personasData?.personas || personasData || []);
        if ((tieneVacacionesColab || esRolAdminGestion) && resultados[3]) {
          setEmpresasColaboradoras((resultados[3] || []).filter(e => e.vacaciones));
        }
        if ((tieneVacacionesColab || esRolAdminGestion) && resultados[4]) {
          setPersonasColab(resultados[4] || []);
        }
      } catch (error) {
        console.error('Error al cargar datos auxiliares:', error);
      }
    };
    cargarDatosAuxiliares();
  }, [puedeVerFiltrosAvanzados, tieneGestion, tieneVacacionesColab, esRolAdminGestion]);

  useEffect(() => {
    if (filtroEmpresa.length > 0) {
      const empresaIds = filtroEmpresa.map(e => e.Id);
      const depsFiltrados = todosDepartamentos.filter(dep =>
        dep.Negocios?.some(emp => empresaIds.includes(emp.Id))
      );
      setDepartamentosFiltrados(depsFiltrados);
      setFiltroDepartamento(prev => prev.filter(d => depsFiltrados.some(df => df.Id === d.Id)));
    } else if (tieneGestion) {
      let depsFiltrados;
      if (esTabColab) {
        depsFiltrados = todosDepartamentos.filter(dep =>
          dep.Negocios?.some(emp => verColabIds.includes(emp.Id))
        );
      } else if (tieneVacacionesInt) {
        // Fase 2 (solo empresa): departamentos de las empresas del ÁMBITO (ver), sin sub-filtro
        // por departamento delegado —ese nivel desapareció—.
        depsFiltrados = todosDepartamentos.filter(dep =>
          dep.Negocios?.some(emp => verNegocioIds.includes(emp.Id))
        );
      } else {
        depsFiltrados = todosDepartamentos;
      }
      setDepartamentosFiltrados(depsFiltrados);
      setFiltroDepartamento(prev => prev.filter(d => depsFiltrados.some(df => df.Id === d.Id)));
    } else {
      setDepartamentosFiltrados(todosDepartamentos);
    }
  }, [filtroEmpresa, todosDepartamentos, tieneGestion, esTabColab, tieneVacacionesInt, verNegocioIds, verColabIds]);

  useEffect(() => {
    const cargarPeriodosVerano = async () => {
      try {
        const data = esTabColab ? await fetchPeriodosVeranoColab() : await fetchPeriodosVerano();
        setPeriodosVerano(data || []);
      } catch (error) {
        console.error('Error al cargar periodos de verano:', error);
      }
    };
    cargarPeriodosVerano();
  }, [esTabColab]);

  const empresasFiltradas = useMemo(() => {
    if (esTabColab) {
      if (ambito?.ver?.todasColaboradoras || esRolAdminGestion) return empresasColaboradoras;
      if (verColabIds.length > 0) return empresasColaboradoras.filter(e => verColabIds.includes(e.Id));
      return empresasColaboradoras;
    }
    if (ambito?.ver?.todosNegocios || esRolAdminGestion) return empresas;
    if (esTabCalendario && verNegocioIds.length > 0) {
      return empresas.filter(e => verNegocioIds.includes(e.Id));
    }
    return empresas;
  }, [empresas, empresasColaboradoras, esTabCalendario, esTabColab, esRolAdminGestion, ambito, verNegocioIds, verColabIds]);

  const personasFiltradas = useMemo(() => {
    if (esTabColab) {
      if (ambito?.ver?.todasColaboradoras || esRolAdminGestion) return personasColab;
      if (verColabIds.length > 0) return personasColab.filter(p => verColabIds.includes(p.empresa_colaboradora_id));
      return personasColab;
    }
    if (ambito?.ver?.todosNegocios || esRolAdminGestion) return personas.filter(p => p.Cargos?.some(c => c.empresa_id && !c.empresa_colaboradores_id));
    if (esTabCalendario && verNegocioIds.length > 0) {
      // Solo EMPRESA (Fase 2): basta con que el cargo esté en una empresa del ámbito (ver);
      // ya no se sub-filtra por departamento (ese nivel desapareció de la delegación).
      return personas.filter(p => p.Cargos?.some(c => verNegocioIds.includes(c.empresa_id) && !c.empresa_colaboradores_id));
    }
    return personas;
  }, [personas, personasColab, esTabCalendario, esTabColab, esRolAdminGestion, ambito, verNegocioIds, verColabIds]);

  const empresasUsuarioFiltradas = useMemo(() => {
    return empresasUsuario;
  }, [empresasUsuario]);

  const esSupervisorEnTab = useMemo(() => {
    if (!esSupervisor || subordinadosIds.length === 0) return false;
    if (!tieneGestion) return true;
    const idsEnTab = new Set(personasFiltradas.map(p => p.Id));
    return subordinadosIds.some(id => idsEnTab.has(id));
  }, [esSupervisor, subordinadosIds, tieneGestion, personasFiltradas]);

  useEffect(() => {
    if (festivos) {
      const todasFechas = [
        ...(festivos.nacional || []).map(f => f.fecha),
        ...(festivos.empresa || []).map(f => f.fecha),
        ...(festivos.provincial || []).map(f => f.fecha),
        ...(festivos.municipal || []).map(f => f.fecha)
      ];
      setFestivosParaCalculo(todasFechas);
    }
  }, [festivos]);

  const empresasOpciones = useMemo(() =>
    [...empresasFiltradas].sort((a, b) => (a.nombre || '').localeCompare(b.nombre || '')),
    [empresasFiltradas]
  );

  const departamentosOpciones = useMemo(() =>
    [...departamentosFiltrados].sort((a, b) => (a.nombre || '').localeCompare(b.nombre || '')),
    [departamentosFiltrados]
  );

  const personasOpciones = useMemo(() => {
    let lista = personasFiltradas.filter(p => p.activo !== false);
    const empresaIds = filtroEmpresa.map(e => e.Id);
    if (esTabColab) {
      if (empresaIds.length > 0) {
        lista = lista.filter(p => empresaIds.includes(p.empresa_colaboradora_id));
      }
    } else {
      if (empresaIds.length > 0) {
        lista = lista.filter(p => p.Cargos?.some(c => empresaIds.includes(c.empresa_id)));
      }
      if (filtroDepartamento.length > 0) {
        const deptIds = filtroDepartamento.map(d => d.Id);
        lista = lista.filter(p => p.Cargos?.some(c => deptIds.includes(c.departamento_id)));
      }
    }
    return lista.sort((a, b) => `${a.nombre} ${a.apellidos}`.localeCompare(`${b.nombre} ${b.apellidos}`));
  }, [personasFiltradas, filtroEmpresa, filtroDepartamento, esTabColab]);

  const empresasEmpOpciones = useMemo(() =>
    [...empresasUsuarioFiltradas].sort((a, b) => (a.nombre || '').localeCompare(b.nombre || '')),
    [empresasUsuarioFiltradas]
  );

  const empresaEmpValue = useMemo(() =>
    empresasUsuarioFiltradas.filter(e => empresasSeleccionadas.includes(e.id)),
    [empresasUsuarioFiltradas, empresasSeleccionadas]
  );

  const departamentosEmpOpciones = useMemo(() =>
    [...departamentosEmpresaFiltrados].sort((a, b) => (a.nombre || '').localeCompare(b.nombre || '')),
    [departamentosEmpresaFiltrados]
  );

  const aplicarFiltros = useCallback(() => {
    setFiltroEmpresaAplicado(filtroEmpresa);
    setFiltroDepartamentoAplicado(filtroDepartamento);
    setFiltroPersonaAplicado(filtroPersona);
  }, [filtroEmpresa, filtroDepartamento, filtroPersona]);

  // Params de ambito/filtros del calendario. Compartido por la carga en pantalla y
  // por la exportacion a Excel, para que el Excel refleje EXACTAMENTE lo que se ve.
  const construirParamsCalendario = useCallback(() => {
    const anio = date.getFullYear();
    const mes = date.getMonth() + 1;
    const params = { anio, estado: filtroEstado, vista: filtroVista };
    if (view !== 'year') {
      params.mes = mes;
    }
    if (filtroPersonaAplicado.length > 0) {
      params.persona_ids = filtroPersonaAplicado.map(p => p.Id).join(',');
    } else if (filtroVista === 'empresa') {
      if (empresasSeleccionadas.length > 0) {
        params.empresa_ids = empresasSeleccionadas.join(',');
      }
      if (departamentosEmpresaSeleccionados.length > 0) {
        params.departamento_ids = departamentosEmpresaSeleccionados.map(d => d.Id).join(',');
      }
    } else {
      if (filtroEmpresaAplicado.length > 0) params.empresa_ids = filtroEmpresaAplicado.map(e => e.Id).join(',');
      if (filtroDepartamentoAplicado.length > 0) params.departamento_ids = filtroDepartamentoAplicado.map(d => d.Id).join(',');
    }

    // Los 4 params de AUTORIZACIÓN (admin_todas, gestion_vacaciones_int/_empresas/_colab) se
    // retiran: INERTES desde 3a (el backend acota por resolverAmbito, verificado por HTTP). Se
    // mantiene `colaborador` (presentación: qué eje/pestaña).
    params.colaborador = esTabColab ? 'true' : 'false';
    return params;
  }, [date, view, filtroEstado, filtroVista, filtroPersonaAplicado, empresasSeleccionadas, departamentosEmpresaSeleccionados, filtroEmpresaAplicado, filtroDepartamentoAplicado, esTabColab]);

  const cargarDatos = useCallback(async () => {
    try {
      setLoading(true);
      const anio = date.getFullYear();

      let vacacionesData = [];
      let festivosData = { nacional: [], empresa: [], provincial: [], municipal: [] };
      let festivosPersonasData = [];

      try {
        const params = construirParamsCalendario();

        vacacionesData = await fetchCalendarioVacaciones(params);

        // Festivos locales de cada persona segun SU calendario (solo en vistas multi-persona)
        if (filtroVista !== 'personal') {
          try {
            const fpParams = { ...params };
            delete fpParams.estado;
            const excluirCal = esTabColab ? calendarioLaboralColabId : calendarioLaboralId;
            if (excluirCal) fpParams.excluir_calendario_id = excluirCal;
            festivosPersonasData = await fetchFestivosPersonas(fpParams);
          } catch (errFp) {
            console.error('Error al cargar festivos de personas:', errFp);
          }
        }
      } catch (err) {
        console.error('Error al cargar vacaciones:', err);
      }

      try {
        if (esTabColab) {
          if (calendarioLaboralColabId) {
            festivosData = await fetchFestivosByCalendarioColab(calendarioLaboralColabId, anio);
          } else {
            festivosData = await fetchFestivosCombinados(anio, null, null, true);
          }
        } else if (tieneVacacionesColab) {
          if (calendarioLaboralId) {
            festivosData = await fetchFestivosByCalendario(calendarioLaboralId, anio);
          } else {
            festivosData = await fetchFestivosCombinados(anio);
          }
        } else {
          if (calendarioLaboralColabId) {
            festivosData = await fetchFestivosByCalendarioColab(calendarioLaboralColabId, anio);
          } else if (calendarioLaboralId) {
            festivosData = await fetchFestivosByCalendario(calendarioLaboralId, anio);
          } else {
            festivosData = await fetchFestivosCombinados(anio);
          }
        }
      } catch (err) {
        console.error('Error al cargar festivos:', err);
      }

      setVacaciones(Array.isArray(vacacionesData) ? vacacionesData : []);
      setFestivos(festivosData || { nacional: [], empresa: [], provincial: [], municipal: [] });
      setFestivosPersonas(Array.isArray(festivosPersonasData) ? festivosPersonasData : []);
    } catch (error) {
      console.error('Error al cargar datos:', error);
      toast.error('Error al cargar el calendario');
    } finally {
      setLoading(false);
    }
  }, [date, view, calendarioLaboralId, calendarioLaboralColabId, esTabColab, tieneVacacionesColab, tieneVacacionesInt, filtroVista, construirParamsCalendario]);

  const [exportando, setExportando] = useState(false);
  const handleExportarExcel = useCallback(async () => {
    try {
      setExportando(true);
      const { desde, hasta } = getRangoVisible(view, date);
      const params = { ...construirParamsCalendario(), desde, hasta };
      const blob = await fetchExportarCalendario(params);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `vacaciones-calendario-${desde}-a-${hasta}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Excel descargado');
    } catch (error) {
      console.error('Error al exportar Excel:', error);
      toast.error('Error al exportar el Excel');
    } finally {
      setExportando(false);
    }
  }, [view, date, construirParamsCalendario]);

  useEffect(() => {
    cargarDatos();
  }, [cargarDatos]);

  useEffect(() => {
    const cargarInfoDashboard = async () => {
      try {
        const [stats, departamentosData] = await Promise.all([
          fetchDashboardStats(),
          fetchDepartamentosData()
        ]);
        setEsSupervisor(stats.es_supervisor || false);
        setSubordinadosIds(stats.subordinados_ids || []);
        if (stats.calendario_laboral_id) {
          setCalendarioLaboralId(stats.calendario_laboral_id);
        }
        // Paso 1: MI calendario (los dos ejes) desde el dashboard, para la marca del popover.
        setMiCalendario({ int: stats.calendario_laboral_id || null, colab: stats.calendario_laboral_colab_id || null });
        if (stats.empresas_usuario) {
          setEmpresasUsuario(stats.empresas_usuario);
        }
        const depsInternos = (departamentosData || []).filter(d => d.tipo === 'interno');
        setDepartamentosEmpresaUsuario(depsInternos);
      } catch (error) {
        console.error('Error al cargar info dashboard:', error);
      }
    };
    cargarInfoDashboard();
  }, []);

  useEffect(() => {
    if (empresasSeleccionadas.length > 0) {
      const depsFiltrados = departamentosEmpresaUsuario.filter(dep =>
        dep.Negocios?.some(emp => empresasSeleccionadas.includes(emp.Id))
      );
      setDepartamentosEmpresaFiltrados(depsFiltrados);
      setDepartamentosEmpresaSeleccionados(prev => prev.filter(d => depsFiltrados.some(df => df.Id === d.Id)));
    } else if (empresasUsuarioFiltradas.length > 0) {
      const empresaIds = empresasUsuarioFiltradas.map(e => e.id);
      const depsFiltrados = departamentosEmpresaUsuario.filter(dep =>
        dep.Negocios?.some(emp => empresaIds.includes(emp.Id))
      );
      setDepartamentosEmpresaFiltrados(depsFiltrados);
      setDepartamentosEmpresaSeleccionados(prev => prev.filter(d => depsFiltrados.some(df => df.Id === d.Id)));
    } else {
      setDepartamentosEmpresaFiltrados(departamentosEmpresaUsuario);
    }
  }, [empresasSeleccionadas, empresasUsuarioFiltradas, departamentosEmpresaUsuario]);

  useEffect(() => {
    if (esAdministracion) {
      setFiltroEstado('aprobada');
      if (!esSupervisor && !tieneGestion && filtroVista !== 'personal') {
        setFiltroVista('personal');
      }
    }
  }, [esAdministracion, esSupervisor, tieneGestion]);

  const events = useMemo(() => {
    const eventosVacaciones = (Array.isArray(vacaciones) ? vacaciones : []).map((v) => {
      let color;
      if (v.departamento?.id) {
        color = getColorDepartamento(v.departamento.id);
      } else {
        color = (v.es_verano || v.es_periodo_verano) ? '#ff9800' : '#4caf50';
      }

      const startDate = parseISO(v.inicio || v.fecha_inicio);
      const endDate = parseISO(v.fin || v.fecha_fin);

      return {
        id: `vac-${v.id || v.Id}`,
        title: v.titulo || `${v.persona?.nombre || ''} ${v.persona?.apellidos || ''}`,
        start: startDate,
        end: addDays(endDate, 1),
        allDay: true,
        resource: v,
        type: 'vacacion',
        color: color,
        departamento: v.departamento
      };
    });

    const festivosNacionales = Array.isArray(festivos?.nacional) ? festivos.nacional : [];
    const festivosEmpresa = Array.isArray(festivos?.empresa) ? festivos.empresa : [];
    const festivosProvinciales = Array.isArray(festivos?.provincial) ? festivos.provincial : [];
    const festivosMunicipales = Array.isArray(festivos?.municipal) ? festivos.municipal : [];

    const eventosFestivos = [
      ...festivosNacionales.map((f) => {
        const fechaFestivo = parseISO(f.fecha);
        return {
          id: `fest-nac-${f.Id || f.id}`,
          title: f.nombre,
          start: fechaFestivo,
          end: addDays(fechaFestivo, 1),
          allDay: true,
          type: 'festivo',
          color: '#e91e63'
        };
      }),
      ...festivosEmpresa.map((f) => {
        const fechaFestivo = parseISO(f.fecha);
        return {
          id: `fest-emp-${f.Id || f.id}`,
          title: f.nombre,
          start: fechaFestivo,
          end: addDays(fechaFestivo, 1),
          allDay: true,
          type: 'festivo',
          color: '#9c27b0'
        };
      }),
      ...festivosProvinciales.map((f) => {
        const fechaFestivo = parseISO(f.fecha);
        return {
          id: `fest-prov-${f.Id || f.id}`,
          title: f.nombre,
          start: fechaFestivo,
          end: addDays(fechaFestivo, 1),
          allDay: true,
          type: 'festivo',
          color: '#673ab7'
        };
      }),
      ...festivosMunicipales.map((f) => {
        const fechaFestivo = parseISO(f.fecha);
        return {
          id: `fest-mun-${f.Id || f.id}`,
          title: f.nombre,
          start: fechaFestivo,
          end: addDays(fechaFestivo, 1),
          allDay: true,
          type: 'festivo',
          color: '#00bcd4'
        };
      })
    ];

    const eventosFestivosPersonas = (Array.isArray(festivosPersonas) ? festivosPersonas : []).map((fp) => {
      const fechaFestivo = parseISO(fp.fecha);
      const color = fp.tipo === 'municipal' ? '#00bcd4' : '#673ab7';
      return {
        id: fp.id,
        title: `${fp.persona?.nombre || ''} ${fp.persona?.apellidos || ''}`.trim(),
        start: fechaFestivo,
        end: addDays(fechaFestivo, 1),
        allDay: true,
        type: 'festivo_persona',
        color,
        resource: fp,
        calendario: fp.calendario
      };
    });

    return [...eventosVacaciones, ...eventosFestivos, ...eventosFestivosPersonas];
  }, [vacaciones, festivos, festivosPersonas]);

  const departamentosUnicos = useMemo(() => {
    const depts = new Map();
    vacaciones.forEach(v => {
      if (v.departamento?.id && !depts.has(v.departamento.id)) {
        depts.set(v.departamento.id, {
          id: v.departamento.id,
          nombre: v.departamento.nombre,
          color: getColorDepartamento(v.departamento.id)
        });
      }
    });
    return Array.from(depts.values());
  }, [vacaciones]);

  const eventStyleGetter = (event) => {
    // El estado se codifica en el RELLENO, no en el color: el eje cromatico ya lo consume
    // la rotacion de departamentos y un color nuevo se lo comeria. Pendiente = rayado
    // diagonal sobre su propio color. Va en backgroundImage para no pisar backgroundColor,
    // y el style en linea de eventPropGetter gana siempre sobre el CSS de .rbc-event.
    const esPendiente = event?.resource?.estado === 'pendiente';
    return {
      style: {
        backgroundColor: event.color,
        ...(esPendiente && {
          backgroundImage: 'repeating-linear-gradient(45deg, transparent 0 3px, rgba(255,255,255,0.55) 3px 6px)'
        }),
        borderRadius: '4px',
        opacity: 0.9,
        color: 'white',
        border: 'none',
        display: 'block',
        fontSize: '12px',
        cursor: 'pointer'
      }
    };
  };

  const handleSelectEvent = (event) => {
    setSelectedEvent(event);
    setShowEventModal(true);
  };

  const handleCloseModal = () => {
    setShowEventModal(false);
    setSelectedEvent(null);
  };

  const handleSelectDayEvents = (events, day) => {
    if (events.length === 1) {
      handleSelectEvent(events[0]);
    } else if (events.length > 1) {
      setDayEvents(events);
      setSelectedDay(day);
      setShowDayEventsModal(true);
    }
  };

  const handleCloseDayEventsModal = () => {
    setShowDayEventsModal(false);
    setDayEvents([]);
    setSelectedDay(null);
  };

  const handleSelectEventFromList = (event) => {
    handleCloseDayEventsModal();
    handleSelectEvent(event);
  };

  const calcularDiasEdit = useCallback(() => {
    if (!editData.fecha_inicio || !editData.fecha_fin) return 0;
    const inicio = new Date(editData.fecha_inicio);
    const fin = new Date(editData.fecha_fin);
    let dias = 0;
    const actual = new Date(inicio);
    while (actual <= fin) {
      const esFinDeSemana = actual.getDay() === 0 || actual.getDay() === 6;
      const fechaStr = actual.toISOString().split('T')[0];
      const esFestivo = festivosParaCalculo.includes(fechaStr);
      if (!esFinDeSemana && !esFestivo) {
        dias++;
      }
      actual.setDate(actual.getDate() + 1);
    }
    return dias;
  }, [editData.fecha_inicio, editData.fecha_fin, festivosParaCalculo]);

  const esPeriodoVeranoEdit = useCallback(() => {
    if (!editData.fecha_inicio || !editData.fecha_fin || periodosVerano.length === 0) return false;
    const fechaInicio = new Date(editData.fecha_inicio);
    const fechaFin = new Date(editData.fecha_fin);
    return periodosVerano.some(periodo => {
      const periodoInicio = new Date(periodo.fecha_inicio);
      const periodoFin = new Date(periodo.fecha_fin);
      return fechaInicio <= periodoFin && fechaFin >= periodoInicio;
    });
  }, [editData.fecha_inicio, editData.fecha_fin, periodosVerano]);

  const getFechaMinFinEdit = () => {
    if (!editData.fecha_inicio) {
      return new Date().toISOString().split('T')[0];
    }
    return editData.fecha_inicio;
  };

  const handleOpenEdit = () => {
    const vacacion = selectedEvent.resource;
    setEditData({
      fecha_inicio: vacacion.inicio?.split('T')[0] || vacacion.fecha_inicio?.split('T')[0] || '',
      fecha_fin: vacacion.fin?.split('T')[0] || vacacion.fecha_fin?.split('T')[0] || '',
      comentarios: vacacion.comentarios || ''
    });
    setShowEventModal(false);
    setShowEditModal(true);
  };

  const handleGuardarEdicion = async () => {
    if (!editData.fecha_inicio || !editData.fecha_fin) {
      toast.warning('Las fechas son obligatorias');
      return;
    }
    const diasCalculados = calcularDiasEdit();
    if (diasCalculados <= 0) {
      toast.error('Los días seleccionados son festivos o fines de semana y no cuentan como días laborables');
      return;
    }
    try {
      const solicitudId = selectedEvent.resource.id || selectedEvent.resource.Id;
      await actualizarSolicitud(solicitudId, {
        ...editData,
        es_periodo_verano: esPeriodoVeranoEdit(),
        dias_solicitados: diasCalculados
      });
      toast.success('Solicitud actualizada correctamente');
      setShowEditModal(false);
      setSelectedEvent(null);
      cargarDatos();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error al actualizar las vacaciones');
    }
  };

  return (
    <div className="gestor-calendario-vacaciones">
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" component="h1" sx={{ fontWeight: 'bold', color: '#336385', mb: 2 }}>
          Calendario
        </Typography>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
{tabsConfig.length > 1 ? (
          <Tabs
            value={tabActual}
            onChange={handleTabChange}
            sx={{
              '& .MuiTab-root': {
                fontWeight: 'bold',
                textTransform: 'none',
                fontSize: '1rem'
              },
              '& .Mui-selected': {
                color: '#336385'
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#336385'
              }
            }}
          >
            {tabsConfig.map((tab) => (
              <Tab key={tab.key} icon={tab.icon} iconPosition="start" label={tab.label} />
            ))}
          </Tabs>
          ) : null}
        </Box>
      </Box>

      {(esTabCalendario || esTabColab) && (
        <>
          {loading ? (
            <div className="gestor-calendario-loading">
              <div className="spinner"></div>
              <p>Cargando calendario...</p>
            </div>
          ) : (
            <>
              <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'center' }}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Typography variant="caption" sx={{ fontWeight: 'bold', mr: 1 }}>Festivos:</Typography>
                  <Chip label="Nacional" sx={{ backgroundColor: '#e91e63', color: 'white' }} size="small" />
                  <Chip label="Empresa" sx={{ backgroundColor: '#9c27b0', color: 'white' }} size="small" />
                  <Chip label="Provincial" sx={{ backgroundColor: '#673ab7', color: 'white' }} size="small" />
                  <Chip label="Municipal" sx={{ backgroundColor: '#00bcd4', color: 'white' }} size="small" />
                  {/* Estado de la solicitud: se explica el RELLENO, no el color. Las dos muestras
                      van en gris azulado neutro a proposito -si usaran un color de la rotacion de
                      departamentos, la leyenda mezclaria los dos ejes visuales-. */}
                  <Typography variant="caption" sx={{ fontWeight: 'bold', ml: 2, mr: 1 }}>Vacaciones:</Typography>
                  <Chip label="Aprobada" size="small" sx={{ backgroundColor: '#607d8b', color: 'white' }} />
                  <Chip
                    label="Pendiente"
                    size="small"
                    sx={{
                      backgroundColor: '#607d8b',
                      color: 'white',
                      backgroundImage: 'repeating-linear-gradient(45deg, transparent 0 3px, rgba(255,255,255,0.55) 3px 6px)'
                    }}
                  />
                </Box>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Button
                  variant="outlined"
                  startIcon={<Download size={18} />}
                  onClick={handleExportarExcel}
                  disabled={exportando}
                  sx={{ borderColor: '#336385', color: '#336385', '&:hover': { borderColor: '#27496d', backgroundColor: 'rgba(51,99,133,0.04)' } }}
                >
                  {exportando ? 'Descargando...' : 'Exportar Excel'}
                </Button>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Vista</InputLabel>
                  <Select
                    value={filtroVista}
                    label="Vista"
                    onChange={(e) => {
                      setFiltroVista(e.target.value);
                      if (e.target.value !== 'todos') {
                        setFiltroEmpresa([]);
                        setFiltroDepartamento([]);
                        setFiltroPersona([]);
                      }
                      if (e.target.value !== 'empresa') {
                        setEmpresasSeleccionadas([]);
                        setDepartamentosEmpresaSeleccionados([]);
                      }
                    }}
                  >
                    {(!esTabColab || esSoloColab) && <MenuItem value="personal">Personal</MenuItem>}
                    {!esTabColab && <MenuItem value="departamento">Mi Departamento</MenuItem>}
                    {(!esTabColab || esSoloColab) && empresasUsuarioFiltradas.length > 0 && (
                      <MenuItem value="empresa">Mi Empresa</MenuItem>
                    )}
                    {/* Fase 3 · en el eje COLABORADOR la condición es `esSupervisorColab`, que
                        viene de /vacaciones/mi-ambito, NO el `esSupervisor` del dashboard: ese
                        deriva de persona_supervisores, que es del eje interno, y para cualquier
                        colaboradora vale false. Dos ejes, cada uno con su flag; reutilizar uno
                        para el otro es la colisión de espacios de id que ya nos costó una tarde. */}
                    {((!esTabColab && esSupervisorEnTab) || (esTabColab && ambito?.esSupervisorColab)) && (
                      <MenuItem value="equipo">Mi Equipo</MenuItem>
                    )}
                    {/* "Mi ámbito" (antes "Todos", que mentía: muestra solo TU alcance, no a todos).
                        Admin (ver-total por rol) lo conserva SIEMPRE —lo sirve el backend aunque
                        mi-ambito falle—; el delegado lo tiene condicionado al ámbito LISTO. */}
                    {(esRolAdminGestion || (ambitoCargado && !ambitoError && (esTabColab ? tieneVacacionesColab : tieneVacacionesInt))) && (
                      <MenuItem value="todos">Mi ámbito</MenuItem>
                    )}
                  </Select>
                </FormControl>
                {/* Fase 4.4 (cambio A): el filtro de estado se queda SOLO en las vistas de cercanía
                    —personal, departamento, equipo—, donde el usuario ve todos los estados de su propia
                    gente. Desaparece en `empresa` y `todos` (ámbito), donde el clamp del backend solo
                    devuelve aprobadas y filtrar por pendiente engañaría (por eso se retiró el aviso).
                    Condición por VISTA, no por usuario: un empleado normal conserva el filtro en sus
                    vistas propias. Trade-off asumido: en la vista de ámbito (que incluye tu depto) no
                    puedes filtrar tu propia gente por pendiente; se hace desde la vista de departamento. */}
                {/* Ampliacion: el filtro vuelve a "Mi ambito" SOLO con gestion TOTAL del eje activo.
                    Con gestion parcial se mantiene oculto, y por el motivo del comentario de arriba:
                    en esa vista conviven la poblacion que gestionas (verias sus pendientes) y la que
                    solo ves (el backend fuerza estado='aprobada', Vacaciones_Controller.js:1847), asi
                    que "Pendientes" devolveria una lista incompleta sin decirlo. Con gestion total del
                    eje esa segunda poblacion no existe y el resultado es integro. */}
                {(filtroVista === 'personal' || filtroVista === 'departamento' || filtroVista === 'equipo'
                  || (filtroVista === 'todos' && (esTabColab
                        ? ambito?.gestionar?.todasColaboradoras
                        : ambito?.gestionar?.todosNegocios))) ? (
                  <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>Estado</InputLabel>
                    <Select
                      value={filtroEstado}
                      label="Estado"
                      onChange={(e) => setFiltroEstado(e.target.value)}
                    >
                      <MenuItem value="">Todos</MenuItem>
                      <MenuItem value="pendiente">Pendientes</MenuItem>
                      <MenuItem value="aprobada">Aprobadas</MenuItem>
                    </Select>
                  </FormControl>
                ) : null}
                </Box>
              </Box>

              {/* Matiz 2: en ERROR el usuario debe enterarse de que sus opciones ampliadas no se
                  han podido cargar —para no leerlo como "me han quitado el ámbito" (revocación)—.
                  Distinto de ejes vacíos legítimos (LISTO sin opciones → sin aviso). Las vistas
                  personal/departamento no dependen del ámbito y siguen en los tres estados. */}
              {ambitoError && (
                <Alert severity="warning" action={<Button color="inherit" size="small" onClick={recargarAmbito}>Reintentar</Button>} sx={{ mb: 2 }}>
                  No se han podido cargar tus opciones de vacaciones; puede que ahora no veas todas las que sueles. Tus vistas personal y de departamento siguen disponibles.
                </Alert>
              )}

              {(puedeVerFiltrosAvanzados || tieneGestion) && filtroVista === 'todos' && (
                <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                  <Autocomplete
                    multiple
                    size="small"
                    sx={{ minWidth: 250 }}
                    options={empresasOpciones}
                    inputValue={empresaInputText}
                    onInputChange={(e, val, reason) => { if (reason !== 'reset') setEmpresaInputText(val || ''); else setEmpresaInputText(''); }}
                    disableCloseOnSelect
                    getOptionLabel={(option) => option.nombre || ''}
                    value={filtroEmpresa}
                    onChange={(e, newValue) => setFiltroEmpresa(newValue)}
                    onClose={() => setTimeout(aplicarFiltros, 0)}
                    renderOption={({ key, ...props }, option, { selected }) => (
                      <li key={key} {...props}>
                        <Checkbox
                          size="small"
                          style={{ marginRight: 8 }}
                          checked={selected}
                        />
                        {option.nombre}
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField {...params} label= {esTabColab ? "Empresas" : "Negocios"} placeholder="Seleccionar..." />
                    )}
                    isOptionEqualToValue={(option, value) => option.Id === value?.Id}
                  />
                  {!esTabColab && (
                  <Autocomplete
                    multiple
                    size="small"
                    sx={{ minWidth: 250 }}
                    options={departamentosOpciones}
                    inputValue={departamentoInputText}
                    onInputChange={(e, val, reason) => { if (reason !== 'reset') setDepartamentoInputText(val || ''); else setDepartamentoInputText(''); }}
                    disableCloseOnSelect
                    getOptionLabel={(option) => option.nombre || ''}
                    value={filtroDepartamento}
                    onChange={(e, newValue) => setFiltroDepartamento(newValue)}
                    onClose={() => setTimeout(aplicarFiltros, 0)}
                    renderOption={({ key, ...props }, option, { selected }) => (
                      <li key={key} {...props}>
                        <Checkbox
                          size="small"
                          style={{ marginRight: 8 }}
                          checked={selected}
                        />
                        {option.nombre}
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField {...params} label="Departamentos" placeholder="Seleccionar..." />
                    )}
                    isOptionEqualToValue={(option, value) => option.Id === value?.Id}
                  />
                  )}
                  <Autocomplete
                    multiple
                    size="small"
                    sx={{ minWidth: 300 }}
                    options={personasOpciones}
                    inputValue={personaInputText}
                    onInputChange={(e, val, reason) => { if (reason !== 'reset') setPersonaInputText(val || ''); else setPersonaInputText(''); }}
                    disableCloseOnSelect
                    getOptionLabel={(option) => `${option.nombre} ${option.apellidos}`}
                    value={filtroPersona}
                    onChange={(e, newValue) => setFiltroPersona(newValue)}
                    onClose={() => setTimeout(aplicarFiltros, 0)}
                    renderOption={({ key, ...props }, option, { selected }) => (
                      <li key={key} {...props}>
                        <Checkbox
                          size="small"
                          style={{ marginRight: 8 }}
                          checked={selected}
                        />
                        {option.nombre} {option.apellidos}
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField {...params} label="Personas" placeholder="Seleccionar..." />
                    )}
                    isOptionEqualToValue={(option, value) => option.Id === value?.Id}
                  />
                  <IconButton
                    size="small"
                    disabled={filtroEmpresa.length === 0 && filtroDepartamento.length === 0 && filtroPersona.length === 0}
                    onClick={() => {
                      setFiltroEmpresa([]);
                      setFiltroDepartamento([]);
                      setFiltroPersona([]);
                      setFiltroEmpresaAplicado([]);
                      setFiltroDepartamentoAplicado([]);
                      setFiltroPersonaAplicado([]);
                      setEmpresaInputText('');
                      setDepartamentoInputText('');
                      setPersonaInputText('');
                    }}
                    sx={{ color: '#336385' }}
                  >
                    <Eraser size={30} />
                  </IconButton>
                </Box>
              )}

              {filtroVista === 'empresa' && (
                <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                  {empresasUsuarioFiltradas.length > 1 && (
                    <Autocomplete
                      multiple
                      size="small"
                      sx={{ minWidth: 300 }}
                      options={empresasEmpOpciones}
                      inputValue={empresaEmpInputText}
                      onInputChange={(e, val, reason) => { if (reason !== 'reset') setEmpresaEmpInputText(val || ''); else setEmpresaEmpInputText(''); }}
                      disableCloseOnSelect
                      getOptionLabel={(option) => option.nombre || ''}
                      value={empresaEmpValue}
                      onChange={(e, newValue) => setEmpresasSeleccionadas(newValue.map(v => v.id))}
                      renderOption={({ key, ...props }, option, { selected }) => (
                        <li key={key} {...props}>
                          <Checkbox
                            size="small"
                            style={{ marginRight: 8 }}
                            checked={selected}
                          />
                          {option.nombre}
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField {...params} label="Filtrar empresas" placeholder="Seleccionar empresas..." />
                      )}
                      isOptionEqualToValue={(option, value) => option.id === value?.id}
                    />
                  )}
                  {!esTabColab && (
                  <Autocomplete
                    multiple
                    size="small"
                    sx={{ minWidth: 300 }}
                    options={departamentosEmpOpciones}
                    inputValue={departamentoEmpInputText}
                    onInputChange={(e, val, reason) => { if (reason !== 'reset') setDepartamentoEmpInputText(val || ''); else setDepartamentoEmpInputText(''); }}
                    disableCloseOnSelect
                    getOptionLabel={(option) => option.nombre || ''}
                    value={departamentosEmpresaSeleccionados}
                    onChange={(e, newValue) => setDepartamentosEmpresaSeleccionados(newValue)}
                    renderOption={({ key, ...props }, option, { selected }) => (
                      <li key={key} {...props}>
                        <Checkbox
                          size="small"
                          style={{ marginRight: 8 }}
                          checked={selected}
                        />
                        {option.nombre}
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField {...params} label="Filtrar departamentos" placeholder="Seleccionar departamentos..." />
                    )}
                    isOptionEqualToValue={(option, value) => option.Id === value?.Id}
                  />
                  )}
                  {(empresasSeleccionadas.length > 0 || departamentosEmpresaSeleccionados.length > 0) && (
                    <Button
                      startIcon={<Eraser />}
                      size="small"
                      onClick={() => {
                        setEmpresasSeleccionadas([]);
                        setDepartamentosEmpresaSeleccionados([]);
                      }}
                      sx={{ color: '#336385' }}
                    >
                    </Button>
                  )}
                </Box>
              )}

      <Card>
        <CardContent>
          {view === 'year' ? (
            <YearView
              date={date}
              events={events}
              onNavigate={setDate}
              onView={setView}
              onSelectEvent={handleSelectEvent}
              onSelectDayEvents={handleSelectDayEvents}
            />
          ) : (
            <Calendar
              localizer={localizer}
              events={events}
              startAccessor="start"
              endAccessor="end"
              style={{ height: 'calc(100vh - 300px)', minHeight: 500 }}
              view={view}
              onView={setView}
              date={date}
              onNavigate={setDate}
              views={['month', 'week', 'day']}
              eventPropGetter={eventStyleGetter}
              onSelectEvent={handleSelectEvent}
              popup
              messages={{
                next: 'Siguiente',
                previous: 'Anterior',
                today: 'Hoy',
                year: 'Año',
                month: 'Mes',
                week: 'Semana',
                day: 'Día',
                noEventsInRange: 'No hay eventos en este periodo',
                showMore: (total) => `+ Ver mas (${total})`,
                allDay: 'Todo el día'
              }}
              culture="es"
              components={{
                toolbar: (toolbarProps) => (
                  <Box sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 1,
                    mb: 2,
                    pb: 1,
                    borderBottom: '1px solid #e0e0e0'
                  }}>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => toolbarProps.onNavigate('TODAY')}
                        startIcon={<TodayIcon />}
                        sx={{ textTransform: 'none' }}
                      >
                        Hoy
                      </Button>
                      <IconButton onClick={() => toolbarProps.onNavigate('PREV')} size="small">
                        <ChevronLeftIcon />
                      </IconButton>
                      <IconButton onClick={() => toolbarProps.onNavigate('NEXT')} size="small">
                        <ChevronRightIcon />
                      </IconButton>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#336385', textTransform: 'capitalize', display: { xs: 'block', md: 'none' } }}>
                        {toolbarProps.label}
                      </Typography>
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#336385', textTransform: 'capitalize', display: { xs: 'none', md: 'block' } }}>
                      {toolbarProps.label}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                      <Button
                        variant="text"
                        size="small"
                        onClick={() => setView('year')}
                        sx={{ textTransform: 'none', minWidth: 'auto', px: 1 }}
                      >
                        Año
                      </Button>
                      <Button
                        variant={view === 'month' ? 'contained' : 'text'}
                        size="small"
                        onClick={() => toolbarProps.onView('month')}
                        sx={{ textTransform: 'none', minWidth: 'auto', px: 1, ...(view === 'month' && { backgroundColor: '#336385' }) }}
                      >
                        Mes
                      </Button>
                      <Button
                        variant={view === 'week' ? 'contained' : 'text'}
                        size="small"
                        onClick={() => toolbarProps.onView('week')}
                        sx={{ textTransform: 'none', minWidth: 'auto', px: 1, ...(view === 'week' && { backgroundColor: '#336385' }) }}
                      >
                        Semana
                      </Button>
                      <Button
                        variant={view === 'day' ? 'contained' : 'text'}
                        size="small"
                        onClick={() => toolbarProps.onView('day')}
                        sx={{ textTransform: 'none', minWidth: 'auto', px: 1, ...(view === 'day' && { backgroundColor: '#336385' }) }}
                      >
                        Día
                      </Button>
                    </Box>
                  </Box>
                )
              }}
            />
          )}
        </CardContent>
      </Card>

      <Dialog open={showEventModal} onClose={handleCloseModal} maxWidth="sm" fullWidth>
        <DialogTitle sx={{
          backgroundColor: selectedEvent?.color || '#336385',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 1 
        }}>
          {selectedEvent?.type === 'vacacion' ? (
            selectedEvent?.resource?.es_verano ? <WbSunnyIcon /> : <BeachAccessIcon />
          ) : (
            <EventIcon />
          )}
          {selectedEvent?.type === 'vacacion' ? 'Detalle de Vacaciones' : 'Festivo'}
        </DialogTitle>
        <DialogContent>
          {selectedEvent && (
            <Box sx={{ mt: 2 }}>
              {selectedEvent.type === 'vacacion' ? (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Avatar
                      src={selectedEvent.resource?.persona?.foto ? `${API_URL}/api/persona/imagen/${selectedEvent.resource.persona.foto}` : null}
                      sx={{ bgcolor: selectedEvent.color, width: 56, height: 56 }}
                    >
                      {!selectedEvent.resource?.persona?.foto && <PersonIcon />}
                    </Avatar>
                    <Box>
                      <Typography variant="h6">
                        {selectedEvent.resource?.persona?.nombre} {selectedEvent.resource?.persona?.apellidos}
                      </Typography>
                      <Chip
                        label={selectedEvent.resource?.es_verano ? 'Periodo de Verano' : 'Vacaciones'}
                        size="small"
                        sx={{ backgroundColor: selectedEvent.color, color: 'white' }}
                      />
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography>
                      <strong>Fecha inicio:</strong> {format(selectedEvent.start, 'dd/MM/yyyy', { locale: es })}
                    </Typography>
                    <Typography>
                      <strong>Fecha fin:</strong> {format(addDays(selectedEvent.end, -1), 'dd/MM/yyyy', { locale: es })}
                    </Typography>
                    <Typography>
                      <strong>Días solicitados:</strong> {selectedEvent.resource?.dias || '-'}
                    </Typography>
                    {/* El color insinúa (sólido/rayado); el modal confirma. */}
                    {selectedEvent.resource?.estado && (
                      <Typography component="div">
                        <strong>Estado:</strong>{' '}
                        <Chip
                          label={selectedEvent.resource.estado === 'pendiente' ? 'Pendiente' : 'Aprobada'}
                          size="small"
                          variant={selectedEvent.resource.estado === 'pendiente' ? 'outlined' : 'filled'}
                          sx={selectedEvent.resource.estado === 'pendiente'
                            ? { borderColor: selectedEvent.color, color: selectedEvent.color }
                            : { backgroundColor: selectedEvent.color, color: 'white' }}
                        />
                      </Typography>
                    )}
                    {/* Fase 3 · el nivel de supervisión, con TEXTO y no con un código de color.
                        El evento ya codifica dos cosas —color = departamento/empresa, relleno
                        rayado = pendiente/aprobada—; una tercera en el mismo rectángulo sería
                        ilegible. Aquí hay sitio para palabras, y el modal es justo el paso
                        previo a intentar la acción: es donde conviene saber si se puede.
                        Gris neutro, el mismo de la leyenda, para no competir con el color de
                        departamento ni con el estado. */}
                    {selectedEvent.resource?.nivel_supervision && (
                      <Typography component="div">
                        <strong>Tu supervisión:</strong>{' '}
                        <Chip
                          label={selectedEvent.resource.nivel_supervision === 'gestionar' ? 'Puedes aprobar' : 'Solo lectura'}
                          size="small"
                          variant={selectedEvent.resource.nivel_supervision === 'gestionar' ? 'filled' : 'outlined'}
                          sx={selectedEvent.resource.nivel_supervision === 'gestionar'
                            ? { backgroundColor: '#607d8b', color: 'white' }
                            : { borderColor: '#607d8b', color: '#607d8b' }}
                        />
                      </Typography>
                    )}
                    {selectedEvent.departamento && (
                      <Typography>
                        <strong>Departamento:</strong>{' '}
                        <Chip
                          label={selectedEvent.departamento.nombre}
                          size="small"
                          sx={{ backgroundColor: selectedEvent.color, color: 'white' }}
                        />
                      </Typography>
                    )}
                    {/* Calendario laboral: SOLO el nombre. La marca "distinto al tuyo" se retiró: con
                        ~44 calendarios en prod (el mayor al 39%) saltaba en la mayoría = ruido. La
                        comparación se movió al modal (Paso 2), sobre los festivos concretos. */}
                    <Typography>
                      <strong>Calendario laboral:</strong> {selectedEvent.resource?.calendario?.nombre || '—'}
                    </Typography>
                    {/* Paso 2: botón "Ver calendario", solo si esa persona tiene calendario asignado. */}
                    {selectedEvent.resource?.calendario?.id && (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<WorkIcon />}
                        onClick={() => cargarFestivosCalModal(
                          selectedEvent.resource.calendario,
                          !!selectedEvent.resource.es_colaborador,
                          `${selectedEvent.resource?.persona?.nombre || ''} ${selectedEvent.resource?.persona?.apellidos || ''}`.trim()
                        )}
                        sx={{ alignSelf: 'flex-start', mt: 0.5, color: '#336385', borderColor: '#336385' }}
                      >
                        Ver calendario
                      </Button>
                    )}
                  </Box>
                </>
              ) : selectedEvent.type === 'festivo_persona' ? (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Avatar
                      src={selectedEvent.resource?.persona?.foto ? `${API_URL}/api/persona/imagen/${selectedEvent.resource.persona.foto}` : null}
                      sx={{ bgcolor: selectedEvent.color, width: 56, height: 56 }}
                    >
                      {!selectedEvent.resource?.persona?.foto && <PersonIcon />}
                    </Avatar>
                    <Box>
                      <Typography variant="h6">
                        {selectedEvent.resource?.persona?.nombre} {selectedEvent.resource?.persona?.apellidos}
                      </Typography>
                      <Chip
                        label="Festivo"
                        size="small"
                        sx={{ backgroundColor: selectedEvent.color, color: 'white' }}
                      />
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography>
                      <strong>Fecha:</strong> {format(selectedEvent.start, 'dd/MM/yyyy', { locale: es })}
                    </Typography>
                    {selectedEvent.resource?.nombre_festivo && (
                      <Typography>
                        <strong>Festivo:</strong> {selectedEvent.resource.nombre_festivo}
                      </Typography>
                    )}
                    {selectedEvent.calendario && (
                      <Typography>
                        <strong>Calendario:</strong>{' '}
                        <Chip
                          label={selectedEvent.calendario.nombre}
                          size="small"
                          variant="outlined"
                        />
                      </Typography>
                    )}
                  </Box>
                </>
              ) : (
                <>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    {selectedEvent.title}
                  </Typography>
                  <Typography>
                    <strong>Fecha:</strong> {format(selectedEvent.start, 'dd/MM/yyyy', { locale: es })}
                  </Typography>
                  <Chip
                    label={selectedEvent.id?.includes('nac') ? 'Nacional' :
                      selectedEvent.id?.includes('emp') ? 'Empresa' :
                      selectedEvent.id?.includes('mun') ? 'Municipal' : 'Provincial'}
                    size="small"
                    sx={{ mt: 1, backgroundColor: selectedEvent.color, color: 'white' }}
                  />
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {/* Fase 3 · dos cambios y los dos hacen falta.
              El OR HABILITA el botón para la supervisora colaboradora sobre las que sí puede
              gestionar: su `esSupervisor` es del eje interno y vale false, y `tieneGestion`
              sale de grants de empresa que no tiene, así que sin esto no lo vería nunca.
              El `!== 'ver'` lo OCULTA sobre las que solo puede mirar: mejor una imposibilidad
              que un aviso, y así no descubre el límite recibiendo un 403.
              Para internos `nivel_supervision` es null, y null !== 'ver' es cierto: no cambia
              nada de lo que ven hoy. */}
          {selectedEvent?.type === 'vacacion' &&
           (['super administrador', 'administrador', 'recursos humanos'].includes(rolLower) ||
             esSupervisor || tieneGestion ||
             selectedEvent?.resource?.nivel_supervision === 'gestionar') &&
           selectedEvent?.resource?.nivel_supervision !== 'ver' &&
           (() => {
             const hoy = new Date();
             hoy.setHours(0, 0, 0, 0);
             const fechaInicio = new Date(selectedEvent?.resource?.inicio || selectedEvent?.resource?.fecha_inicio);
             fechaInicio.setHours(0, 0, 0, 0);
             return fechaInicio >= hoy;
           })() && (
            <Button
              variant="contained"
              onClick={handleOpenEdit}
              sx={{ backgroundColor: '#336385', '&:hover': { backgroundColor: '#27496d' } }}
            >
              Editar
            </Button>
          )}
          <Button onClick={handleCloseModal} sx={{ color: selectedEvent?.color || '#336385' }}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Paso 2: modal del calendario del compañero, apilado sobre el Dialog de detalle.
          Mismo lenguaje de tipos que el resto de la pantalla (Nacional/Empresa/Provincial/Municipal). */}
      <Dialog open={showCalModal} onClose={() => setShowCalModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#336385', color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
          <WorkIcon />
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>
              {calModalInfo?.calNombre || 'Calendario laboral'}
            </Typography>
            <Typography variant="caption">
              {calModalInfo?.personaNombre ? `${calModalInfo.personaNombre} · ` : ''}{date.getFullYear()}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {calModalLoading ? (
            <Typography sx={{ py: 4, textAlign: 'center', color: '#666' }}>Cargando festivos…</Typography>
          ) : calModalError ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography sx={{ mb: 2, color: '#666' }}>No se pudieron cargar los festivos.</Typography>
              <Button variant="outlined" onClick={() => cargarFestivosCalModal(
                { id: calModalInfo?.calId, nombre: calModalInfo?.calNombre }, calModalInfo?.esColab, calModalInfo?.personaNombre
              )}>
                Reintentar
              </Button>
            </Box>
          ) : (() => {
            const f = calModalFestivos || {};
            const grupos = [
              { key: 'nacional', label: 'Nacional', color: '#e91e63', items: f.nacional || [] },
              { key: 'empresa', label: 'Empresa', color: '#9c27b0', items: f.empresa || [] },
              { key: 'provincial', label: 'Provincial', color: '#673ab7', items: f.provincial || [] },
              { key: 'municipal', label: 'Municipal', color: '#00bcd4', items: f.municipal || [] },
            ];
            const total = grupos.reduce((n, g) => n + g.items.length, 0);
            if (total === 0) {
              return <Typography sx={{ py: 3, textAlign: 'center', color: '#666' }}>
                Este calendario no tiene festivos registrados para {date.getFullYear()}.
              </Typography>;
            }
            return (
              <Box sx={{ mt: 1 }}>
                {calModalMisFechas && (
                  <Typography variant="caption" sx={{ display: 'block', mb: 1.5, color: '#666' }}>
                    Los marcados <strong>no son festivos en tu calendario</strong>.
                  </Typography>
                )}
                {grupos.filter(g => g.items.length).map(g => (
                  <Box key={g.key} sx={{ mb: 2 }}>
                    <Chip label={g.label} size="small" sx={{ backgroundColor: g.color, color: 'white', mb: 1 }} />
                    {g.items.map((fest, i) => {
                      const soloSuyo = calModalMisFechas && !calModalMisFechas.has(fest.fecha);
                      return (
                        <Typography key={i} variant="body2" sx={{ ml: 0.5, fontWeight: soloSuyo ? 600 : 400 }}>
                          {(fest.fecha || '').split('-').reverse().join('/')} — {fest.nombre}
                          {soloSuyo && (
                            <Chip label="no lo tienes" size="small" color="warning" variant="outlined"
                              sx={{ ml: 1, height: 18, '& .MuiChip-label': { px: 0.75, fontSize: 11 } }} />
                          )}
                        </Typography>
                      );
                    })}
                  </Box>
                ))}
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCalModal(false)} sx={{ color: '#336385' }}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showDayEventsModal} onClose={handleCloseDayEventsModal} maxWidth="sm" fullWidth>
        <DialogTitle sx={{
          backgroundColor: '#336385',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <CalendarMonthIcon />
          {selectedDay && format(selectedDay, "d 'de' MMMM 'de' yyyy", { locale: es })}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 2, color: '#666' }}>
              {dayEvents.length} eventos en este día
            </Typography>
            {dayEvents.map((event, idx) => (
              <Box
                key={idx}
                onClick={() => handleSelectEventFromList(event)}
                sx={{
                  p: 2,
                  mb: 1,
                  borderRadius: 1,
                  backgroundColor: '#f5f5f5',
                  borderLeft: `4px solid ${event.color}`,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': {
                    backgroundColor: '#e8e8e8',
                    transform: 'translateX(4px)'
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  {event.type === 'vacacion' ? (
                    event.resource?.es_verano ? <WbSunnyIcon sx={{ color: event.color, fontSize: 20 }} /> : <BeachAccessIcon sx={{ color: event.color, fontSize: 20 }} />
                  ) : (
                    <EventIcon sx={{ color: event.color, fontSize: 20 }} />
                  )}
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                    {event.title}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Chip
                    label={event.type === 'vacacion' ? (event.resource?.es_verano ? 'Verano' : 'Vacaciones') :
                      event.type === 'festivo_persona' ? 'Festivo' :
                      (event.id?.includes('nac') ? 'Nacional' :
                       event.id?.includes('emp') ? 'Empresa' :
                       event.id?.includes('mun') ? 'Municipal' : 'Provincial')}
                    size="small"
                    sx={{ backgroundColor: event.color, color: 'white', fontSize: '0.7rem' }}
                  />
                  {event.type === 'vacacion' && event.departamento && (
                    <Chip
                      label={event.departamento.nombre}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: '0.7rem' }}
                    />
                  )}
                  {event.type === 'festivo_persona' && event.calendario && (
                    <Chip
                      label={event.calendario.nombre}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: '0.7rem' }}
                    />
                  )}
                </Box>
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDayEventsModal} sx={{ color: '#336385' }}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showEditModal} onClose={() => setShowEditModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#336385', color: 'white' }}>
          Editar Solicitud de Vacaciones
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            margin="normal"
            label="Fecha Inicio"
            type="date"
            value={editData.fecha_inicio}
            onChange={(e) => {
              const nuevaFechaInicio = e.target.value;
              const fechaFinActual = editData.fecha_fin;
              if (fechaFinActual && fechaFinActual < nuevaFechaInicio) {
                setEditData({ ...editData, fecha_inicio: nuevaFechaInicio, fecha_fin: '' });
              } else {
                setEditData({ ...editData, fecha_inicio: nuevaFechaInicio });
              }
            }}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: new Date().toISOString().split('T')[0] }}
            required
          />
          <TextField
            fullWidth
            margin="normal"
            label="Fecha Fin"
            type="date"
            value={editData.fecha_fin}
            onChange={(e) => setEditData({ ...editData, fecha_fin: e.target.value })}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: getFechaMinFinEdit() }}
            required
          />
          {editData.fecha_inicio && editData.fecha_fin && (
            <Box sx={{ my: 2, p: 2, backgroundColor: esPeriodoVeranoEdit() ? '#ffedd5' : '#e3f2fd', borderRadius: 1 }}>
              <Typography variant="body1">
                <strong>Días laborables solicitados:</strong> {calcularDiasEdit()}
              </Typography>
              {esPeriodoVeranoEdit() && (
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                  <BeachAccessIcon sx={{ color: '#ea580c', mr: 1 }} />
                  <Typography variant="body2" sx={{ color: '#ea580c', fontWeight: 500 }}>
                    Incluye días de período de verano
                  </Typography>
                </Box>
              )}
            </Box>
          )}
          <TextField
            fullWidth
            margin="normal"
            label="Comentarios"
            multiline
            rows={3}
            value={editData.comentarios}
            onChange={(e) => setEditData({ ...editData, comentarios: e.target.value })}
          />
        </DialogContent>
        {selectedEvent?.resource?.estado === 'aprobada' && (
          <Box sx={{ px: 3, pb: 2 }}>
            <Alert severity="warning">
              Al editar una solicitud aprobada, volverá al estado <strong>pendiente</strong> y requerirá nueva aprobación.
            </Alert>
          </Box>
        )}
        <DialogActions>
          <Button onClick={() => setShowEditModal(false)} sx={{ color: '#333' }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleGuardarEdicion}
            sx={{ backgroundColor: '#336385', '&:hover': { backgroundColor: '#27496d' } }}
          >
            Guardar Cambios
          </Button>
        </DialogActions>
      </Dialog>
            </>
          )}
        </>
      )}

      {tabActual === tabLaboralIndex && tabLaboralIndex !== -1 && (
        <GestorCalendariosLaborales />
      )}

      {tabActual === tabLaboralColabIndex && tabLaboralColabIndex !== -1 && (
        <GestorCalendariosLaborales esColab={true} />
      )}

      <ToastContainer />
    </div>
  );
}

export default GestorCalendarioVacaciones;
